import { useMemo } from 'react';
import { withStyles, Card, CircleButton, Icon, MaterialSymbol } from 'vopay';

import { useTranslation } from 'react-i18next';
import { useDialog } from '#shared/providers/Dialog';
import { useToast } from '#shared/providers/Toast';
import { useBlockingLoader } from '#shared/providers/Loader';
import useLabels from '../hooks/useLabels';
import Chip from './Chip';
import AddLabelDialog from './AddLabelDialog/AddLabelDialog';
import styles from './CustomerOverview.module.scss';

import { Title } from '#shared/components/UI';

const cn = withStyles(styles);

export type OverviewProps = {
  accountID: string;
  active: boolean;
  accountName: string;
  lang: string;
  isBusiness: boolean;
  // Host fact the portal read from its ClientData provider.
  clientAccountsEnabled: boolean;
};

// The portal's customer overview card (avatar, name, active badge, labels),
// ported for the contact drawer.
export default function CustomerOverview({
  accountID,
  active,
  accountName,
  lang,
  isBusiness,
  clientAccountsEnabled,
}: OverviewProps) {
  const {
    labelsLoaded,
    labels: customerLabels,
    fetchLabels,
    deleteLabel,
  } = useLabels({ accountID, clientAccountsEnabled });

  const dialog = useDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.Overview' });

  async function handleAddLabelClick() {
    if (!customerLabels) return;

    const addedLabel = await dialog.open<string>(
      ({ onConfirm, onCancel }) => (
        <AddLabelDialog
          customerLabels={customerLabels}
          clientAccountsEnabled={clientAccountsEnabled}
          onClose={onCancel}
          onAdd={onConfirm}
          accountID={accountID}
        />
      ),
      // The dialog card supplies its own surface.
      { bare: true },
    );

    if (addedLabel) {
      blockingLoader.show();

      try {
        await fetchLabels();
      } catch {
        // Logged by the hook; the list refreshes on the next fetch.
      } finally {
        blockingLoader.hide();
      }

      toast.success(
        t('labelAdded', {
          defaultValue: `${addedLabel} Label added to ${accountName}`,
          addedLabel,
          accountName,
        }),
      );
    }
  }

  async function handleDeleteLabelClick(removedLabel: string) {
    blockingLoader.show();

    try {
      await deleteLabel(removedLabel);

      toast.neutral(
        t('labelRemoved', {
          defaultValue: `${removedLabel} Label Removed from ${accountName}`,
          removedLabel,
          accountName,
        }),
      );
    } catch {
      // Logged by the hook.
      toast.neutral(
        t('labelRemoveFail', {
          defaultValue: `Failed to remove ${removedLabel} Label from ${accountName}`,
          removedLabel,
          accountName,
        }),
      );
    } finally {
      blockingLoader.hide();
    }
  }

  const avatar = useMemo(() => {
    if (isBusiness) {
      return active ? 'businessAvatar' : 'businessAvatarInactive';
    }

    return active ? 'personAvatar' : 'personAvatarInactive';
  }, [isBusiness, active]);

  return (
    <Card className={cn('accountSummaryCard')}>
      <div className={cn('accountSummary')}>
        <Icon icon={avatar} size="xlg" width="64px" height="64px" color="none" />

        <div className={cn('accountNameAndType')}>
          <div className={cn('accountNameAndLang')}>
            <Title level="4" className={cn('accountName')}>
              {accountName}
            </Title>

            <span className={cn('langBadge')}>
              <MaterialSymbol icon="language" size="17px" />
              {lang}
            </span>
          </div>

          <div className={cn('accountType')}>
            {isBusiness ? t('business', { defaultValue: 'Business' }) : t('individual', { defaultValue: 'Individual' })}
          </div>
        </div>

        <div className={cn('activeBadge', active && 'active')}>
          <Icon icon={active ? 'checkGreen' : 'inactive'} color="none" size="sm" width="20px" height="20px" />
          {active ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })}
        </div>
      </div>

      {labelsLoaded && (
        <div data-testid="label-container" className={cn('labelContainer')}>
          <div className={cn('labels', 'scrollbar')}>
            {customerLabels.length > 0
              ? customerLabels.map((label) => (
                  <Chip
                    disabled={!active}
                    key={label}
                    label={label}
                    {...(active && { iconRight: 'close' })}
                    onClick={() => handleDeleteLabelClick(label)}
                  />
                ))
              : t('noLabels', { defaultValue: 'No Labels' })}
          </div>

          {active && <CircleButton icon="add" size="large" onClick={handleAddLabelClick} circleFill={false} />}
        </div>
      )}
    </Card>
  );
}
